import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, Overline } from "@/components/ui-bits";
import { ScrollText, Download, Filter } from "lucide-react";

const FILTERS = [
  { value: "", label: "All" },
  { value: "critical", label: "Critical" },
  { value: "warn", label: "Warnings" },
  { value: "info", label: "Info" },
];

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => api.get(`/audit${filter ? `?severity=${filter}` : ""}`).then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [filter]);
  useRealtime(() => load());

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aether-audit-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Forensics & Audit Trail</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Audit & Forensics</h1>
        </div>
        <button data-testid="export-audit-btn" onClick={exportJson} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-700 text-slate-200 font-mono text-[11px] uppercase tracking-wider hover:border-slate-500 transition-colors">
          <Download className="w-4 h-4" /> Export JSON
        </button>
      </div>

      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-slate-500" />
        {FILTERS.map((f) => (
          <button key={f.value} data-testid={`filter-${f.value || "all"}`} onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-md font-mono text-[10px] uppercase tracking-wider border transition-colors ${filter === f.value ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-400" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="hud-card rounded-lg overflow-hidden">
        <div className="max-h-[calc(100vh-18rem)] overflow-y-auto" data-testid="audit-log">
          {logs.length === 0 && (
            <div className="p-12 text-center text-slate-600 font-mono text-xs"><ScrollText className="w-8 h-8 mx-auto mb-2" /> No audit events.</div>
          )}
          <div className="divide-y divide-slate-800/60">
            {logs.map((l) => (
              <div key={l.id} className="flex items-start gap-3 px-5 py-3 hover:bg-slate-800/30 transition-colors" data-testid={`audit-row-${l.id}`}>
                <StatusBadge status={l.severity} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-cyan-300">{l.action}</span>
                    <span className="font-mono text-[11px] text-slate-500">→ {l.target}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{l.actor} · {l.detail}</div>
                </div>
                <div className="font-mono text-[10px] text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
